/**
 * Рантайм-флаги фич (DEV-1291).
 *
 * Источник — `/features.json`, который nginx отдаёт из ConfigMap `vp-features`
 * (сам ConfigMap живёт в cloud/k8s-infrastructure/features/<env>.json). Это
 * позволяет включать и выключать фичу в проде **без пересборки, без нового тега
 * и без деплоя**: MR на одну строку → ~3 минуты → готово.
 *
 * Почему не `import.meta.env`: VITE_*-переменные зашиваются в бандл на этапе
 * сборки — они задаются через ARG в Dockerfile, поэтому смена значения требует
 * пересборки образа и полного цикла релиза, ровно того, от чего флаг и должен
 * избавлять.
 *
 * Значения читаются один раз на старте и фиксируются на время сессии: чтобы
 * подхватить переключение, пользователю достаточно обновить страницу.
 */

let flags: Record<string, boolean> = {}

/**
 * Загружает флаги. Вызывается один раз в main.ts ДО монтирования приложения —
 * иначе неготовая фича успеет мигнуть на экране и исчезнуть.
 *
 * Никогда не бросает: любая проблема означает «флагов нет», то есть все фичи
 * выключены — **fail-closed**. Недоступность конфига не должна показывать
 * пользователю то, что ещё не готово.
 *
 * Здесь это важнее, чем в CRM: nginx публичного сайта отдаёт `index.html`
 * на любой неизвестный путь (`try_files $uri $uri/ /index.html`), поэтому при
 * отсутствии файла придёт HTML с кодом 200, а не 404. Проверка «пришёл объект»
 * — единственное, что отделяет это от «все флаги включены».
 */
export async function loadFeatures(): Promise<void> {
  try {
    const res = await fetch('/features.json', { cache: 'no-store' })
    if (!res.ok) throw new Error(`HTTP ${res.status}`)
    const data: unknown = await res.json()
    flags =
      data && typeof data === 'object' && !Array.isArray(data)
        ? (data as Record<string, boolean>)
        : {}
  } catch {
    flags = {}
  }

  // В dev-режиме флаги можно переопределить локально, не трогая кластер:
  //   localStorage.setItem('ff', '{"dev-1234-my-feature":true}')
  if (import.meta.env.DEV) {
    try {
      Object.assign(flags, JSON.parse(localStorage.getItem('ff') ?? '{}'))
    } catch {
      /* мусор в localStorage не должен ломать загрузку приложения */
    }
  }
}

/** Включена ли фича. Неизвестный ключ — всегда `false`. */
export const isEnabled = (key: string): boolean => flags[key] === true

/** Только для тестов: подменить набор флагов. */
export const __setFeaturesForTest = (next: Record<string, boolean>): void => {
  flags = next
}
